import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Focus Supplements - produktsidans hjälpkomponenter.
 *
 * <fs-variant-refresh data-key="...">  Byter innehåll när kunden väljer en annan smak
 *                                       (Horizon skickar med hela sektionens nya HTML).
 * <fs-quantity-breaks>                  Mängdväljaren (1-4 burkar, valfri smak per burk).
 *                                       product-form.js frågar den efter rader via getBatchItems().
 * <fs-bundle-builder>                   Paketväljaren: välj smak per del och lägg paketet som en rad.
 * <fs-add-button data-variant-id>       Lägg-till-knapp för "Köp ihop"-kort (en select inuti väljer smak).
 */

/**
 * Lägger rader i kundvagnen och berättar det för temat (kundvagnslådan, ikonen och sticky-knappen).
 * @param {Array<{variantId: string|number, quantity: number, properties?: Record<string, string>}>} items
 * @param {HTMLElement} source
 */
export async function fsAddLines(items, source) {
  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.variantId), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
          ...(item.properties ? { properties: item.properties } : {}),
        })),
        sections: sectionIds.join(','),
      }),
    });
    const data = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
    const failed = Boolean(data.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'fs-pdp',
        sourceId: source.id || 'fs-pdp',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: failed ? undefined : data.sections,
        didError: failed,
      },
    });
    if (failed) {
      source.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
      return { ok: false, message: data.description || data.message };
    }
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: '' };
  }
}

class FsVariantRefresh extends HTMLElement {
  #target = null;

  connectedCallback() {
    this.#target = this.closest('[id*="ProductInformation-"]');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html || typeof html.querySelector !== 'function') return;
        const fresh = html.querySelector(`fs-variant-refresh[data-key="${this.dataset.key}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/** Sparar kundens val i mängdväljaren när blocket ritas om efter smakbyte. */
const quantityState = new Map();

class FsQuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#restore();
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get #tier() {
    const checked = this.querySelector('input[data-tier]:checked');
    return checked ? Number(checked.value) : 1;
  }

  get #selects() {
    return Array.from(this.querySelectorAll('select[data-unit]'));
  }

  #onChange = (event) => {
    if (event?.target?.matches?.('select[data-unit="1"]')) {
      this.#syncMainVariant(event.target.value);
      // 1 burk: visa den nya smakens bilder direkt (inte vid flera burkar)
      if (this.#tier <= 1) this.#scrollToGallery();
    }
    this.#update();
    quantityState.set(this.dataset.key, {
      tier: this.#tier,
      picks: this.#selects.map((select) => select.value),
    });
  };

  #restore() {
    const saved = quantityState.get(this.dataset.key);
    if (!saved) return;
    const radio = this.querySelector(`input[data-tier][value="${saved.tier}"]`);
    if (radio) radio.checked = true;
    this.#selects.forEach((select, index) => {
      // Burk 1 följer alltid smaken som är vald ovanför
      if (index === 0) return;
      const value = saved.picks[index];
      if (value && select.querySelector(`option[value="${value}"]:not([disabled])`)) select.value = value;
    });
  }

  /** Scrollar upp till produktbilderna om de inte syns (mobil, där bilderna ligger ovanför). */
  #scrollToGallery() {
    const section = this.closest('.shopify-section');
    const gallery = section?.querySelector('media-gallery, .product-information__media');
    if (!gallery) return;
    const rect = gallery.getBoundingClientRect();
    if (rect.bottom > 80 && rect.top < window.innerHeight) return;
    const header = document.querySelector('#header-component');
    const offset = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    window.scrollTo({ top: rect.top + window.scrollY - offset - 8, behavior: 'smooth' });
  }

  /** Burk 1 styr produktens valda smak: välj samma variant i temats (dolda) smakväljare. */
  #syncMainVariant(variantId) {
    const section = this.closest('.shopify-section');
    const select = section?.querySelector('variant-picker select');
    if (!select) return;
    const index = Array.from(select.options).findIndex((option) => option.dataset.variantId === String(variantId));
    if (index < 0 || index === select.selectedIndex) return;
    select.selectedIndex = index;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #update() {
    const tier = this.#tier;
    this.toggleAttribute('data-multi', tier > 1);
    const title = this.querySelector('[data-units-title]');
    if (title) title.hidden = tier <= 1;
    for (const label of this.querySelectorAll('[data-label-single]')) {
      label.textContent = tier > 1 ? label.dataset.labelMulti : label.dataset.labelSingle;
    }
    this.#selects.forEach((select, index) => {
      const row = select.closest('[data-unit-row]');
      if (row) row.hidden = index >= tier;
    });
  }

  /**
   * Anropas av product-form.js. Returnerar null vid 1 burk (vanligt köp).
   * @returns {Array<{variantId: string, quantity: number}> | null}
   */
  getBatchItems() {
    const tier = this.#tier;
    if (tier <= 1) return null;
    const counts = new Map();
    this.#selects.slice(0, tier).forEach((select) => {
      counts.set(select.value, (counts.get(select.value) || 0) + 1);
    });
    return Array.from(counts, ([variantId, quantity]) => ({ variantId, quantity }));
  }
}

class FsAddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = async () => {
    const button = this.button;
    if (!button || button.disabled) return;
    const label = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const variantId = this.querySelector('select')?.value || this.dataset.variantId;
    const result = await fsAddLines([{ variantId, quantity: 1 }], this);
    button.removeAttribute('aria-busy');
    if (result.ok) {
      button.textContent = this.dataset.addedLabel || '✓';
      setTimeout(() => {
        button.innerHTML = label;
        button.disabled = false;
      }, 1800);
    } else {
      button.innerHTML = label;
      button.disabled = false;
    }
  };
}

class FsBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add]');
    this.button?.addEventListener('click', this.#onAdd);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onAdd);
  }

  #onAdd = async () => {
    const button = this.button;
    const error = this.querySelector('[data-bundle-error]');
    if (!button || button.disabled) return;
    const properties = {};
    let missing = false;
    for (const select of this.querySelectorAll('select[data-slot]')) {
      const option = select.selectedOptions[0];
      if (!select.value || !option) {
        missing = true;
        continue;
      }
      properties[select.dataset.slot] = option.dataset.label || option.textContent.trim();
      properties[`_${select.dataset.slot} variant`] = select.value;
    }
    const included = this.dataset.included;
    if (included) properties[this.dataset.includedLabel || 'Ingår'] = included;
    if (missing) {
      if (error) {
        error.hidden = false;
        error.textContent = this.dataset.missingText || '';
      }
      return;
    }
    if (error) error.hidden = true;
    const label = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const result = await fsAddLines([{ variantId: this.dataset.variantId, quantity: 1, properties }], this);
    button.removeAttribute('aria-busy');
    button.disabled = false;
    button.innerHTML = label;
    if (!result.ok && error) {
      error.hidden = false;
      error.textContent = result.message || this.dataset.errorText || '';
    }
  };
}

/** Smakval i sticky-raden: styr mängdväljarens burk 1 (eller temats smakväljare). */
class FsStickyFlavour extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.select?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onChange);
  }

  #onChange = () => {
    const section = this.closest('.shopify-section');
    const value = this.select.value;
    const unitSelect = section?.querySelector('fs-quantity-breaks select[data-unit="1"]');
    if (unitSelect) {
      unitSelect.value = value;
      unitSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const main = section?.querySelector('variant-picker select');
    if (!main) return;
    const index = Array.from(main.options).findIndex((option) => option.dataset.variantId === value);
    if (index < 0) return;
    main.selectedIndex = index;
    main.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

/** Förloppslinje för produktkarusellen. */
class FsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.bar = this.querySelector('[data-progress]');
    this.track?.addEventListener('scroll', this.#update, { passive: true });
    window.addEventListener('resize', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#update);
    window.removeEventListener('resize', this.#update);
  }

  #update = () => {
    const { track, bar } = this;
    if (!track || !bar) return;
    const visible = track.clientWidth / track.scrollWidth;
    const max = track.scrollWidth - track.clientWidth;
    const progress = max > 0 ? track.scrollLeft / max : 0;
    bar.parentElement.hidden = visible >= 1;
    bar.style.width = `${Math.max(visible, 0.1) * 100}%`;
    bar.style.transform = `translateX(${progress * (1 / Math.max(visible, 0.1) - 1) * 100}%)`;
  };
}

if (!customElements.get('fs-carousel')) customElements.define('fs-carousel', FsCarousel);
if (!customElements.get('fs-sticky-flavour')) customElements.define('fs-sticky-flavour', FsStickyFlavour);
if (!customElements.get('fs-bundle-builder')) customElements.define('fs-bundle-builder', FsBundleBuilder);
if (!customElements.get('fs-variant-refresh')) customElements.define('fs-variant-refresh', FsVariantRefresh);
if (!customElements.get('fs-quantity-breaks')) customElements.define('fs-quantity-breaks', FsQuantityBreaks);
if (!customElements.get('fs-add-button')) customElements.define('fs-add-button', FsAddButton);
